import { Group, Stack } from '@mantine/core';
import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { useTranslation } from '@apple/lib/i18next';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';

('@apple/ui/link/Link');

import { StoreEventDetails } from '@apple/features/store-events/components/details';
import { LineFilters } from '@apple/features/store-events/components/line-filters';
import { CategoryLinesTable } from '@apple/features/store-events/components/lines/category-lines-table';
import { ShipmentReportLink } from '@apple/features/store-events/components/shipment-report-link';
import {
	EventProductsFormProvider,
	useEventProductsForm,
} from '@apple/features/store-events/hooks/use-event-products-form';
import { useSelectedCategories } from '@apple/features/store-events/hooks/use-selected-categories';
import {
	DEFAULT_STORE_EVENT_LINE_FILTERS,
	type StoreEventLineFilters,
} from '@apple/features/store-events/models/lines';
import { storeEventQueries } from '@apple/features/store-events/queries';

export const Route = createFileRoute('/_authed/store-events/_steps_/$storeEventId/')({
	validateSearch: z.object({ readOnly: z.boolean() }).partial(),
	beforeLoad: async ({
		params: { storeEventId },
		search: { readOnly },
		context: { auth, queryClient },
	}) => {
		const canWrite = auth.hasPermission(
			'AppleRetail.Security.Features.StoreEventManagement.Write',
		);

		const storeEvent = await queryClient.ensureQueryData(
			storeEventQueries.getById(storeEventId),
		);

		if (readOnly || !canWrite || storeEvent.isInFinalState) {
			// Show the current page...
			return;
		}
		if (storeEvent.isOpen) {
			throw redirect({
				to: '/store-events/$storeEventId/products',
				params: {
					storeEventId: storeEventId,
				},
			});
		}

		throw redirect({
			to: '/store-events/$storeEventId/details',
			params: {
				storeEventId: storeEventId,
			},
		});
	},
	component: ReadOnlyStoreEventRouteComponent,
	wrapInSuspense: true,
});

function ReadOnlyStoreEventRouteComponent() {
	const { t } = useTranslation('store-event');
	const { storeEventId } = Route.useParams();
	const [filters, setFilters] = useState<StoreEventLineFilters>(() => ({
		storeEventId,
		...DEFAULT_STORE_EVENT_LINE_FILTERS,
	}));
	const [storeEventQuery, linesByCategory] = useSuspenseQueries({
		queries: [
			storeEventQueries.getById(storeEventId),
			storeEventQueries.linesByCategory(filters),
		],
	});
	const erpDetailsQuery = useQuery(
		storeEventQueries.lineFulfillmentInfo(
			storeEventId,
			linesByCategory.data?.items.flatMap(x => x.category.id) ?? [],
			storeEventQuery.data?.hasErpDetails ?? false,
		),
	);
	const { categoryIds } = useSelectedCategories(storeEventId);

	// TODO: Find a way to not need the form since this page is readonly (CategoryLineRow uses the form context)
	const { form } = useEventProductsForm(storeEventId);

	return (
		<EventProductsFormProvider form={form}>
			<DetailsLayout title={t('labels.storeEvent')} fluid>
				<StoreEventDetails
					id={storeEventQuery.data.id}
					hasShipmentReport={storeEventQuery.data.hasErpDetails}
					storeId={storeEventQuery.data.storeNumber ?? undefined}
					storeUtcOffsetMinutes={storeEventQuery.data.storeUtcOffsetMinutes}
					readOnly
					storeEvent={storeEventQuery.data}
					renderShipmentReportLink={ShipmentReportLink}
				/>

				<Group align='flex-start' mt='xl'>
					<Stack flex={1}>
						{linesByCategory.data.items.map(categoryLines => (
							<CategoryLinesTable
								key={categoryLines.category.id}
								storeEventId={storeEventId}
								categoryLines={categoryLines}
								erpDetails={
									!storeEventQuery.data?.hasErpDetails ? [] : erpDetailsQuery.data
								}
								erpDetailsLoading={erpDetailsQuery.isPending}
							/>
						))}
					</Stack>

					<LineFilters
						filters={filters}
						setFilters={setFilters}
						categoryIds={categoryIds}
					/>
				</Group>
			</DetailsLayout>
		</EventProductsFormProvider>
	);
}
